import { Component, EventEmitter, HostListener, OnInit, OnDestroy, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TemplateService } from '../../services/template.service';
import { ToastService } from '../../../../core/services/toast.service';
import { Router } from '@angular/router';
import { BehaviorSubject, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, timeout, catchError } from 'rxjs/operators';
import { AuthService } from '../../../../core/services/auth.service';
import { LogActionDTO, LoginLogsService } from '../../../../shared/components/layout/dashboard/loginLogs/login-logs.service';

/**
 * Added after the main prompt (promptTemplate below) only while coding mode is on, so normal chat requests and the
 * saved template never carry it. It adjusts the main prompt's lines for an answer shown as red and green lines.
 */
export const CODING_MODE_PROMPT = `CODING MODE: every request is a coding task shown on screen as red and green lines, not read aloud - skip the answer shape, the client scenario and bold, keep the code rules above for new code, and never use "-- old" / "++ new" markers.
A request about the same thing as the current code (same table, same program), even a full sentence ("fetch the users whose age is more than 21", "who shirt color is blue", "not blue, yellow", "from x to y", "in descending order", "fix it"), or the learner's own code, is newProblem false - start from currentFiles, keep every condition and line the request does not change, make exactly the change asked for and add nothing else, always returning the complete code; never answer that nothing needs to change unless the request only asks to check code that is already correct; only a different program or problem (another task, another language, or "new" / "another") is newProblem true with a fresh solution and baseline null.
Only code shown in a screenshot or pasted in the request goes into baseline (never currentFiles, which is already on screen) - copy it exactly as written, mistakes included, with the fixed version in files under the same ids; check every line (syntax, operators, method names, indentation, names, loop bounds, returns, casing), fix every mistake and nothing else, and write one note per change - anchor is the changed line copied exactly, text says what was wrong, why it fails and what replaces it (for a new solution, one note per section).
Language: the one asked for, else the screenshot's, else sql for a query, else the one being discussed (not just the previous answer's), else defaultLanguage - never ask, never JavaScript or TypeScript unless asked for or shown; summary is 1-3 plain sentences (for a fix, count and name the mistakes) with code words in \`backticks\` exactly as in the code, clarification only when the screenshot is unreadable.`;

@Component({
  selector: 'app-template-form',
  templateUrl: './template-form.component.html',
  styleUrls: ['./template-form.component.scss']
})
export class TemplateFormComponent implements OnInit, OnDestroy {
    isSubmitting = false;
  private directCodeInputTimeout: any;
  @Output() templateCreated = new EventEmitter<any>();
  private loggedIn = new BehaviorSubject<boolean>(false);
  private searchSubject = new Subject<string>();
  private searchSubscription: any;
  form!: FormGroup;
  copied = false;
  searchQuery = '';
  // Creation-date filter (yyyy-MM-dd from native date inputs, interpreted in the viewer's local timezone)
  fromDate = '';
  toDate = '';
  fields = [
    { name: 'consultantName', placeholder: 'Consultant Name', label: 'Consultant Name', type: 'text', required: true },
    { name: 'presentRole', placeholder: 'Present Role', label: 'Present Role', type: 'text', required: true },
    { name: 'interviewingRole', placeholder: 'Interviewing Role', label: 'Interviewing Role', type: 'text', required: true },
    { name: 'interviewingClient', placeholder: 'Interviewing Client', label: 'Interviewing Client', type: 'text', required: true },
    { name: 'presentClient', placeholder: 'Present Client', label: 'Present Client', type: 'text', required: true },
    { name: 'previousClient', placeholder: 'Previous Client', label: 'Previous Client', type: 'text', required: true },
    {
      name: 'interviewType',
      placeholder: 'Interview Type',
      label: 'Interview Type',
      type: 'select',
      required: true,
      options: ['practice', 'screening', 'firstround', 'finalround']
    },
    {
      name: 'jobDescription',
      placeholder: 'e.g. Deep learning, distributed systems...',
      label: 'Job Description',
      type: 'textarea',
      required: true
    }
  ];
  isDropdownOpen = false;
  prompts: any[] = [];
  displayedPrompts: any[] = [];
  selectedPrompt: any = null;
  loadingPrompts = false;
  directCode: string = '';
  directCodeError: string = '';
  sidebarHidden = true;
  isMobile: boolean = false;
  menuOpen: boolean = false;
  customSelectOpen: { [key: string]: boolean } = {};

  // Pagination state
  currentPage = 0;
  pageSize = 10;
  totalPages = 1;
  totalElements = 0;
  pageSizeOptions = [10, 20, 30, 50, 100];
  loginLogsService: LoginLogsService;
  constructor(
    private fb: FormBuilder,
    private templateService: TemplateService,
    private router: Router,
    private authService: AuthService,
    private toastService: ToastService,
    loginLogsService: LoginLogsService
  ) { this.loginLogsService = loginLogsService; }

  ngOnInit(): void {
    this.buildForm();
    this.fetchPrompts();
    this.checkScreenSize();

    // Live search with debounce — resets to page 0 on new search
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((query: string) => {
        this.loadingPrompts = true;
        this.currentPage = 0;
        return this.templateService.searchPromptsPageable(
          query, 0, this.pageSize, this.getFromEpoch(), this.getToEpoch()
        ).pipe(
          timeout(15000),
          catchError(() => {
            this.loadingPrompts = false;
            return of(null);
          })
        );
      })
    ).subscribe({
      next: (response: any) => {
        if (response) {
          this.handlePageResponse(response);
        }
        this.loadingPrompts = false;
      },
      error: () => {
        this.loadingPrompts = false;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  buildForm(): void {
    const group: { [key: string]: any } = {};
    this.fields.forEach(field => {
      const validators = field.required ? [Validators.required] : [];
      group[field.name] = ['', validators];
    });
    this.form = this.fb.group(group);
  }

  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
  }
  toggleMenu() {
    this.menuOpen = !this.menuOpen;
  }

  toggleCustomSelect(fieldName: string, event: Event): void {
    event.stopPropagation();
    const wasOpen = this.customSelectOpen[fieldName];
    // Close all first
    Object.keys(this.customSelectOpen).forEach(k => this.customSelectOpen[k] = false);
    this.customSelectOpen[fieldName] = !wasOpen;
  }

  selectCustomOption(fieldName: string, value: string): void {
    this.form.get(fieldName)?.setValue(value);
    this.customSelectOpen[fieldName] = false;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    Object.keys(this.customSelectOpen).forEach(k => this.customSelectOpen[k] = false);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkScreenSize();
  }

  checkScreenSize() {
    this.isMobile = window.innerWidth <= 1024;
  }
  fetchPrompts(): void {
    this.loadingPrompts = true;
    this.displayedPrompts = [];
    this.templateService.searchPromptsPageable(
      this.searchQuery, this.currentPage, this.pageSize,
      this.getFromEpoch(), this.getToEpoch()
    ).pipe(
      timeout(15000),
      catchError(() => {
        this.loadingPrompts = false;
        return of(null);
      })
    ).subscribe({
      next: (response: any) => {
        if (response) {
          this.handlePageResponse(response);
        }
        this.loadingPrompts = false;
      }
    });
  }

  /** Handle paginated API response */
  private handlePageResponse(response: any): void {
    if (response && response.content) {
      // Paginated response
      this.prompts = response.content;
      this.displayedPrompts = response.content;
      this.totalElements = response.totalElements || 0;
      this.totalPages = response.totalPages || 1;
      this.currentPage = response.number || 0;
    } else if (Array.isArray(response)) {
      // Fallback: non-paginated array response
      this.prompts = response;
      this.displayedPrompts = response;
      this.totalElements = response.length;
      this.totalPages = 1;
      this.currentPage = 0;
    }
  }

  onSearchInput(): void {
    this.searchSubject.next(this.searchQuery);
  }

  /** Convert the "From" date (yyyy-MM-dd) to the start-of-day epoch in the viewer's local timezone */
  private getFromEpoch(): number | null {
    if (!this.fromDate) return null;
    const d = new Date(`${this.fromDate}T00:00:00`);
    return isNaN(d.getTime()) ? null : d.getTime();
  }

  /** Convert the "To" date (yyyy-MM-dd) to the end-of-day epoch in the viewer's local timezone */
  private getToEpoch(): number | null {
    if (!this.toDate) return null;
    const d = new Date(`${this.toDate}T23:59:59.999`);
    return isNaN(d.getTime()) ? null : d.getTime();
  }

  /** Re-run the search from page 0 whenever the date range changes */
  onDateFilterChange(): void {
    this.currentPage = 0;
    this.fetchPrompts();
  }

  /** Clear the date range and reload */
  clearDateFilter(): void {
    this.fromDate = '';
    this.toDate = '';
    this.currentPage = 0;
    this.fetchPrompts();
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages) return;
    this.currentPage = page;
    this.fetchPrompts();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 0;
    this.fetchPrompts();
  }

  /** Generate visible page numbers with ellipsis */
  get visiblePages(): (number | '...')[] {
    const pages: (number | '...')[] = [];
    const total = this.totalPages;
    const current = this.currentPage;

    if (total <= 5) {
      for (let i = 0; i < total; i++) pages.push(i);
      return pages;
    }

    pages.push(0);
    if (current > 2) pages.push('...');

    const start = Math.max(1, current - 1);
    const end = Math.min(total - 2, current + 1);
    for (let i = start; i <= end; i++) pages.push(i);

    if (current < total - 3) pages.push('...');
    pages.push(total - 1);

    return pages;
  }

  selectPrompt(prompt: any): void {
    this.selectedPrompt = prompt;
    this.form.patchValue({
      consultantName: prompt.consultantName || '',
      presentRole: prompt.presentRole || '',
      interviewingRole: prompt.interviewingRole || '',
      interviewingClient: prompt.interviewingClient || '',
      presentClient: prompt.presentClient || '',
      previousClient: prompt.previousClient || '',
      interviewType: prompt.interviewType || '',
      jobDescription: prompt.jobDescription || ''
    });
  }

  clearSelection(): void {
    this.selectedPrompt = null;
    this.form.reset();
  }


  reloadChat(): void {
    if (this.selectedPrompt) {
      this.router.navigate(['/chat'], { queryParams: { templateId: this.selectedPrompt.id } });
    }
  }
  toggleSidebar(): void {
    this.sidebarHidden = !this.sidebarHidden
  }
  logout(): void {
    this.loggedIn.next(false); // Broadcast that the user is logged out
    const loginLogId = sessionStorage.getItem('loginLogId');
    if (loginLogId) {
      this.loginLogsService.logUpdate(loginLogId, () => {
        this.authService.signOut();
      });
    } else {
      this.authService.signOut();
      localStorage.removeItem('isLoggedIn');
      this.router.navigate(['/auth/sign-in']);
    }
  }

  onSubmit(): void {
    if (this.form.invalid || this.isSubmitting) return;

    this.isSubmitting = true;
    const formValues = this.form.value;

    let promptTemplate = `ROLE

You're a 10-year ${formValues.presentRole} named ${formValues.consultantName} - raised and educated in India, currently at ${formValues.presentClient}, previously ${formValues.previousClient}.
Interviewing for ${formValues.interviewingRole}.
Interview Type: ${formValues.interviewType}
Job Description: ${formValues.jobDescription}

Talk like yourself - don't perform American casualness, don't perform polish.

This output is read aloud live off a teleprompter.
Write for the ear, not the eye.
Each complete sentence goes on its own line, with blank lines between thought-groups.
Don't split sentences mid-flow at commas or dashes - those create speech pauses but stay on the same line.
Use contractions naturally (we're, it's, don't).
Numbers spoken casually - "40, 50 DAGs" not "forty to fifty".
Technical names with dots/underscores/camelCase rephrased into speakable form ("the shuffle partitions config", not the literal string).

FORMATTING - BOLD KEYWORDS
Always wrap important technical terms, tool names, and key concepts in **bold** using markdown.
Examples: **Kubernetes**, **CI/CD**, **Prometheus**, **partitioning**, **blue-green deployment**.
This makes the output scannable on the teleprompter - bold words are anchor points for the eye.
Do NOT over-bold — only the 2-4 most important terms per answer.

Stay within what you've been told.
You know: name is ${formValues.consultantName}, around 10 years total, currently at ${formValues.presentClient}, before that at ${formValues.previousClient}.
You don't know: tenure split, college, city, team names.
Keep those vague - "currently at ${formValues.presentClient}, before that at ${formValues.previousClient}", not "4 years at ${formValues.presentClient}, 6 at ${formValues.previousClient}, started in Bangalore".

CONTEXT AWARENESS
Carry the conversation topic forward naturally — if the interviewer asks "what is Java" then says "write two programs", write Java programs.
But when the interviewer switches to a NEW topic (e.g., asks about Angular after discussing Java), answer ONLY about the new topic.
Do NOT mix content from previous unrelated answers into new ones — each new topic gets a fresh answer based only on that topic.

Answer shape - 3 to 5 sentences then STOP.
Pick the strongest scenario or mechanism, not two.

1. Open with the production insight, not a definition.
Skip definition openers ("X is when...", "X is basically...") and skip framing-style essay openers that announce what you're about to say ("The real value of X is...", "The core constraint in X is...", "The insight that matters is...").
State the production truth directly: "Airflow is only a scheduler, the moment logic goes into a DAG file you've lost" or "the expensive part in shuffle isn't computation, it's disk and network."

2. Anchor in one real ${formValues.presentClient} or ${formValues.previousClient} scenario with specific numbers.
Go deep on the actual mechanism - name the specific component, config, technique that solved it.
Surface-level answers are worse than missing answers.
Include production texture where natural - legacy code, team pushback, migration timelines, things that didn't work first.
Don't perform humility or drama - no lines like "the person who got woken up at 2am".

3. End conclusively.
State the outcome plainly and stop.
Never offer to go deeper or ask which part they want.
If the same question is asked twice, cover a different angle.

Intros - 4 parts on separate line blocks:
(1) "Hi, I'm ${formValues.consultantName}. I have around 10 years of experience as a ${formValues.presentRole}."
(2) Current at ${formValues.presentClient} with concrete stack and scale.
(3) "Before ${formValues.presentClient} I was at ${formValues.previousClient}" + one sharp contrast line (${formValues.previousClient} correctness-focused, ${formValues.presentClient} scale-focused).
(4) One plain line about main area of work.
Don't say you're interviewing for the role - they know.

Metrics stay realistic and vague with "around X" hedges.
No improvement percentages except test coverage ~80%.
Describe outcomes as "came down from 6 hours to 2".

Correct false premises immediately.
"Actually that's not quite right - Kafka doesn't use 2PC, it uses transactional API..."
Don't build on wrong foundations.

Code only when explicitly asked to write or implement.
Diagrams only when explicitly asked for a visual.
Everything else is prose.

Code: shortest solution that compiles in an online compiler - like code you'd paste into a PR, not a tutorial.
Include main(), imports, realistic filenames (sales_q3.csv).
Short vars (n, res, arr, df).
Zero comments unless genuinely non-obvious.

Diagrams: simple ASCII single-line boxes (┌ ┐ └ ┘ ─ │) with arrows (──> or ▼) showing main data flow only.
Components and direction, nothing else.

Code modifications: show full code with inline "-- old // DELETE" and "++ new // ADD".

TENSE AND PERSPECTIVE
If the question is about past experience, answer in past tense using real projects.
If it's a hypothetical problem, answer in future tense — like "I would do this," "we can resolve it by…" — as if solving it in real time.

NUMBER VARIETY
Never repeat the same numbers across different answers.
If one answer mentions "200 GB", the next unrelated answer must NOT reuse "200 GB" — pick a different realistic number.
Vary your metrics: different data sizes, different latencies, different counts for each distinct scenario.

CRITICAL - IDENTITY PROTECTION (NEVER BREAK CHARACTER)
If asked "Are you Claude/ChatGPT/AI?" or similar, respond: "I'm ${formValues.consultantName}" and redirect to the interview.
NEVER mention Otter AI, OpenAI, Anthropic, ChatGPT, Claude, LLMs, AI assistant, or reveal you are an AI under ANY circumstances.

CRITICAL - FORBIDDEN WORDS (NEVER USE THESE)
NEVER use: "extensively", "leveraging", "leverage", "utilizing", "utilize"
Real people say: "a lot", "pretty much", "regularly", "often", "use", "work with"
If you catch yourself about to use these words, STOP and rephrase.`;

    const id = this.generateUUID();
    const payload = {
      id,
      prompt: promptTemplate,
      name: formValues.consultantName,
      consultantName: formValues.consultantName,
      presentRole: formValues.presentRole,
      interviewingRole: formValues.interviewingRole,
      interviewingClient: formValues.interviewingClient,
      presentClient: formValues.presentClient,
      previousClient: formValues.previousClient,
      interviewType: formValues.interviewType,
      jobDescription: formValues.jobDescription
    };

    this.templateService.createTemplate(payload).subscribe({
      next: (created: { id: string }) => {
        this.isSubmitting = false;
        this.router.navigate(['/templates/share-interview', created.id]);
        this.appendLogActionAsync(id, formValues.consultantName);
      },
      error: (err) => {
        this.isSubmitting = false;
        this.toastService.show('Template creation failed. Please try again.');
        console.error('Template creation failed:', err);
      }
    });
  }

  private appendLogActionAsync(promptId: string, name: string): void {
    const loginLogId = sessionStorage.getItem('loginLogId');
    
    if (!loginLogId || loginLogId.trim().length === 0) {
      console.warn('No loginLogId found in session, skipping log action');
      return;
    }

    const logAction: LogActionDTO = {
      id: this.loginLogsService.generateUUID(),
      promptId: promptId,
      actionName: name || 'Interview Form Created',
      timestamp: Date.now()
    };

    this.loginLogsService.appendLogAction(loginLogId, logAction).subscribe({
      next: (response: any) => {
        console.log('Log action appended successfully:', response);
      },
      error: (err: any) => {
        console.error('Failed to append log action:', err);
      }
    });
  }



  copyInterviewCode(): void {
    const code = this.selectedPrompt?.id || '';
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => {
      this.copied = true;
      setTimeout(() => {
        this.copied = false;
      }, 1000);
    })
  }

  goToChatWithCode(code: string): void {
    if (!code || !/^[0-9]{6}$/.test(code)) {
      this.directCodeError = 'Interview code must be exactly 6 digits.';
      setTimeout(() => { this.directCodeError = ''; }, 3000);
      return;
    }
    this.directCodeError = '';
    this.templateService.getTemplateById(code).subscribe({
      next: (template) => {
        this.router.navigate(['/chat'], { queryParams: { templateId: code } });
      },
      error: (err) => {
        this.directCodeError = 'Invalid interview code. Please check and try again.';
        this.toastService.show('Invalid interview code. Please check and try again.', 'error', 4000);
        setTimeout(() => {
          this.directCodeError = '';
        }, 3000);
      }
    });

  }

  onDirectCodeInput(): void {
    if (this.directCodeInputTimeout) {
      clearTimeout(this.directCodeInputTimeout);
    }
    this.directCodeError = '';
    this.directCodeInputTimeout = setTimeout(() => {
      if (!/^[0-9]{6}$/.test(this.directCode)) {
        this.directCodeError = 'Interview code must be exactly 6 digits.';
      }
    }, 5000);
  }

  onDirectCodeKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.goToChatWithCode(this.directCode);
    }
  }

  generateUUID(): string {
    let num: string;
    num = Math.floor(100000 + Math.random() * 900000).toString();
    return num;
  }

  navigateToPrompt(promptId: any): void {
    this.router.navigate(['/chat'], { queryParams: { templateId: promptId } });
  }


}
